import * as React from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { IStore } from '../../../../../../redux/IStore';
import { flow, map, get, size } from 'lodash';
import { showDestinations, showRegions } from '../../../../../../data/api/api';
import { IDestination, IPolicyApplication, IProduct, IQuotes, ITraveller } from '../../../../../../data/api/models';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import FaInfoCircle from 'react-icons/fa/info-circle';
import { II18nMessages } from '../../../../../../services/branding/models';

interface ISidebarQuoteSummary {
  i18n: II18nMessages;
  policyApplication: IPolicyApplication;
  destinations: IDestination[];
  values: IPolicyApplication;
  product: IProduct;
  quotes: IQuotes;
  channel: any;
  quote: any;
  isAdditionalCover: boolean;
}

class SidebarQuoteSummary extends React.Component<ISidebarQuoteSummary> {

  private destReMap = (dest) => {
    if (dest === 'Europe 1') {
      dest = 'Europe';
    }
    if (dest === 'Europe 2') {
      dest = 'Europe';
    }
    if (dest === 'Worldwide Exc USA/Canada/Caribbean') {
      dest = 'Worldwide Excluding';
    }
    if (dest === 'Worldwide Inc USA/Canada/Caribbean') {
      dest = 'Worldwide';
    }
    return dest;
  }

  private renderDestinations = () => {
    const { values, product } = this.props;

    let content = (
      <p>Nothing Selected</p>
    );

    let regionTripType = values.tripType;

    if (product.productCode === 'LSB') {
      regionTripType = 'longstay';
    }

    if (showDestinations(regionTripType) && values.destinations
    ) {
      content = (
        <div>
          {product.metadata.regions[ values.region ]}
        </div>
      );
    }

    if (showRegions(regionTripType) && values.region) {
      content = (
        <div>{product.metadata.regions[ values.region ]}</div>
      );
    }

    if (product.productCode === 'CSIS' || product.productCode === 'TPL' || product.productCode === 'CAMC') {
      if (regionTripType === 'annual') {
        const dest = this.destReMap(product.metadata.regions[ values.region ]);
        content = (
            <div>{dest}</div>
        );
      } else {
        if (values.destinations && values.destinations.length > 0) {
          const destinationList = values.destinations;
          content = (
              <div>{destinationList.join(', ')}</div>
          );
        }
      }
    }

    return (
      <div className="quote-summary-section">
        <span>
          { ['FPS', 'FPR', 'NPO', 'NPOR', 'CSPA', 'CSPAR', 'CSS', 'CSSR'].includes(product.productCode) ? (
              'Destination:'
          ) : showDestinations(regionTripType) ?
            (values.destinations && values.destinations.length > 1 ? 'Destinations:' : 'Destination:') : 'Region:'
          }
        </span>
        <div className="quote-summary-info">
          {content}
        </div>
      </div>
    );
  }

  private renderTravelDate() {
    const { values, product } = this.props;

    let content = null;

    if (values.startDate) {
      if (['FPS', 'FPR', 'NPO', 'NPOR', 'CSPA', 'CSPAR', 'CSS', 'CSSR'].includes(product.productCode)) {
        content = (
          <div>
            {values.startDate} {values.endDate ? ' - ' + values.endDate : ''}
          </div>
        );
      } else {
        content = (
          <div>
            {values.startDate}
            {values.tripType === 'single' && values.endDate ? ' - ' + values.endDate : ''}
          </div>
        );
      }
    }

    return (
      <div className="quote-summary-section">
        <span>
          {['FPS', 'FPR', 'NPO', 'NPOR', 'CSPA', 'CSPAR', 'CSS', 'CSSR'].includes(product.productCode) ? (
              'Travel Dates:'
          ) : (
            values.tripType === 'single' ? 'Travel Dates:' : 'Start Date:'
          )}
        </span>
        <div className="quote-summary-info">
          {content}
        </div>
      </div>
    );
  }

  private renderTraveller = (type: string, display: string) => (traveller: ITraveller, key) => {
    let conditions = null;
    let dob = '';

    if (traveller.screening) {
      conditions = (
        <span className="traveller-conditions"><br />{traveller.screening.meta.conditions.join(', ')}</span>
      );
    }

    if (traveller.dob) {
      dob = `(${traveller.dob})`;
    }

    return (
      <div key={key}>
        {display} {key + 1}: <em>{traveller.title} {traveller.firstName} {traveller.lastName}</em> {dob}
        {conditions}
      </div>
    );
  }

  private renderTravellers = () => {
    const { values, product } = this.props;
    const { travellers } = values;

    if (!travellers || !values.tripType) {
      return null;
    }

    const noTravellerDetailsEntered = travellers[0].title === '' || travellers[0].firstName === '';
    const adults = travellers.filter((traveller) => traveller.type === 'adult');
    const children = travellers.filter((traveller) => traveller.type === 'child');
    const groupTypes = product.metadata[ `group_type_trip_${values.tripType}` ];
    const total = adults.length + children.length;
    let summaryMessage = `${adults.length} ${adults.length > 1 ? 'adults' : 'adult'}`;

    if (children.length) {
      summaryMessage += `, ${children.length} ${children.length > 1 ? 'children' : 'child'}`;
    }

    return (
      <div className="quote-summary-section">
        <span>
          {total > 1 ? 'Travellers:' : 'Traveller:'}
        </span>
        {!noTravellerDetailsEntered && (
          <div className="quote-summary-info">
            <div>{groupTypes[ values.groupType ]} ({summaryMessage})</div>

            {adults.map(this.renderTraveller('adult', 'Adult'))}
            {children.map(this.renderTraveller('child', 'Child'))}
          </div>
        )}
      </div>
    );
  }

  private renderTripType() {
    const { values, quotes, product } = this.props;
    const content = 'Nothing Selected';
    const regionTripType = values.tripType;

    if (product.productCode === 'CSIS' || product.productCode === 'TPL' || product.productCode === 'CAMC') {
      return (
          <div className="quote-summary-section">
            Policy Type:
            <div className="quote-summary-info">
              <div>
                <p>
                  {regionTripType ? regionTripType : content}
                </p>
              </div>
            </div>
          </div>
      );
    }

    return (
        <div className="quote-summary-section">
          Cover Choice:
          <div className="quote-summary-info">
            <div>
              {size(quotes) === 1 && map(quotes, (item, key) => {
                return map(item, (i, key2) => {
                  return (<span key={`${key}-${key2}`}>{i.scheme.name}<br/></span>);
                });
              })}
            </div>
          </div>
        </div>
    );
  }

  private renderResidency() {
    const { values } = this.props;

    let residency = '';

    if (values.residency === 'uk') {
      residency = 'United Kingdom';
    } else if (values.residency) {
      residency = values.residency.toLowerCase()
          .split(' ')
          .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
          .join(' ');
    }

    return (
        <div className="quote-summary-section">
          Residency:
          <div className="quote-summary-info">
            <div>
              {<span>{residency}</span>}
            </div>
          </div>
        </div>
    );
  }

  private renderAdditionalOptions = () => {
    const { values, channel, quote } = this.props;
    const getAllOptions = get(quote, 'application.premiums[0].information.availableOptions');
    const isOnList = ['tpl-b2c', 'camc-b2c', 'csis-b2c'].includes(channel.key);

    const options = map(values.options, (value: any, key) => {
      if (key === 'cruise_cover' && !isOnList) {
        return;
      }

      const productOption = getAllOptions && getAllOptions[ key ];
      const getOptionsList = getAllOptions && getAllOptions[ key ];
      const getOptionSelected = getOptionsList && getOptionsList.options &&
        getOptionsList.options.find((o) => o.key === value);
      if (productOption && productOption.input_type && productOption.input_type === 'enum') {
        return (
          <div key={key}>{productOption && productOption.name}: {getOptionSelected && getOptionSelected.name}</div>
        );
      }

      if (productOption && productOption.name && productOption.input_type
        && productOption.input_type === 'boolean' && value === true) {
        return (
          <div key={key}>{productOption && productOption.name}</div>
        );
      }

      if (productOption && productOption.name && productOption.input_type === 'list') {
        const item = productOption.items[ value ];

        const popover = (
          <Popover
            id={`popover-summary-${key}`}
          >
            {item}
          </Popover>
        );

        const info = (
          <OverlayTrigger
            trigger="click"
            rootClose={true}
            overlay={popover}
            placement="right"
            positionLeft={200}
            positionTop={50}
          >
            <FaInfoCircle className="pointer" />
          </OverlayTrigger>
        );

        return (
          <div key={key}>
            {productOption.name} {info}
          </div>
        );
      }
    }).filter((option) => !!option);

    if (options.length === 0) {
      return null;
    }

    return (
      <div className="quote-summary-section">
        <span>
          Additional {options.length > 1 ? 'Options:' : 'Option:'}
        </span>
        <div className="quote-summary-info">
          {options}
        </div>
      </div>
    );
  }

  private renderQuoteReference = () => {
    const { values } = this.props;

    return (
      <div className="quote-summary-section">
        <span>
          Quote Reference:
        </span>
        <div className="quote-summary-info">
          {values.quoteReference}
        </div>
      </div>
    );
  }

  private renderActivities = () => {
    const { values } = this.props;
    const activities = [];

    if (values.activities && values.activities.length > 0) {
        values.activities.map((a) => (
            activities.push(a + ', ')
        ));

        const lastActivity = activities[activities.length - 1];
        activities[activities.length - 1] = lastActivity.slice(0, -2);

        return (
            <div className="quote-summary-section">
                <span>
                  Activities:
                </span>
                <div className="quote-summary-info">
                    {activities}
                </div>
            </div>
        );
    }
  }

  public render() {
    return (
      <div className="quote-details">
        {this.renderTripType()}
        {this.renderDestinations()}
        {this.renderTravelDate()}
        {this.renderTravellers()}
        {this.props.isAdditionalCover && this.renderTripType()}
        {/*{this.renderResidency()}*/}
        {this.renderAdditionalOptions()}
        {this.renderQuoteReference()}
        {this.renderActivities()}
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const { quotes, complete } = state.quote;
      const values = selector(
        state,
        'quote',
        'destinations',
        'startDate',
        'endDate',
        'region',
        'tripType',
        'options',
        'travellers',
        'groupType',
        'schemeId',
        'quoteReference',
        'activities',
        'residency',
      );

      return {
        i18n: state.branding.i18n,
        quote: state.quote,
        channel: state.branding.channel,
        policyApplication: state.quote.application,
        destinations: state.quote.destinations,
        values,
        quotes,
        complete,
      };
    },
  ),
])(SidebarQuoteSummary);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/components/QuoteSidebar/components/SidebarQuoteSummary/index.tsx